import type { Step, Substep } from "@views/Upholstery/types"

/**
 * Copy per-car Dato design perforation `defaultValue` onto merged steps.
 *
 * Steps V2 replaces the per-car Design step with a shared one whose CMS
 * `value` is often the substep key (e.g. "perforation"), not the OEM default
 * (e.g. "p3"). Without this overlay, initial selection falls back to p0/None.
 */
function normalizeDefault(raw: unknown): string {
  if (raw == null) return ""
  if (typeof raw === "string") return raw.trim()
  if (typeof raw === "object" && raw !== null && "optionValue" in raw) {
    return String((raw as { optionValue?: string }).optionValue ?? "").trim()
  }
  return String(raw).trim()
}

function isUsablePerforationDefault(
  defaultValue: string,
  substepKey: string
): boolean {
  if (!defaultValue) return false
  const lower = defaultValue.toLowerCase()
  // Steps V2 often stores the substep key/title as `value` — not a real default.
  if (lower === substepKey.toLowerCase()) return false
  if (lower === "perforation" || lower === "color-insert-perforation") {
    return false
  }
  return true
}

function findSubstep(step: Step | undefined, key: string): Substep | undefined {
  return step?.substeps?.find((s) => s.key === key)
}

function overlayDefaultOntoSubstep(
  target: Substep | undefined,
  sourceDefault: string
): Substep | undefined {
  if (!target || !sourceDefault) return target
  return { ...target, defaultValue: sourceDefault }
}

export function overlayDatoDesignDefaultsOnSteps(
  mergedSteps: Step[],
  datoCarSteps: Step[]
): Step[] {
  const datoDesign = datoCarSteps.find((s) => s.key === "design")
  if (!datoDesign?.substeps?.length) return mergedSteps

  const datoPerf = findSubstep(datoDesign, "perforation")
  const datoColorInsertPerf = findSubstep(
    datoDesign,
    "color-insert-perforation"
  )

  const perfDefault = normalizeDefault(datoPerf?.defaultValue)
  const colorInsertDefault = normalizeDefault(datoColorInsertPerf?.defaultValue)
  const nestedPerfDefault = normalizeDefault(
    datoColorInsertPerf?.substeps?.find((n) => n.key === "perforation")
      ?.defaultValue
  )

  const usablePerf = isUsablePerforationDefault(perfDefault, "perforation")
    ? perfDefault
    : ""
  const usableColorInsert = isUsablePerforationDefault(
    colorInsertDefault,
    "color-insert-perforation"
  )
    ? colorInsertDefault
    : ""
  const usableNested = isUsablePerforationDefault(
    nestedPerfDefault,
    "perforation"
  )
    ? nestedPerfDefault
    : ""

  if (!usablePerf && !usableColorInsert && !usableNested) {
    return mergedSteps
  }

  return mergedSteps.map((step) => {
    if (step.key !== "design") return step

    return {
      ...step,
      substeps: (step.substeps ?? []).map((substep) => {
        if (substep.key === "perforation" && usablePerf) {
          return overlayDefaultOntoSubstep(substep, usablePerf) ?? substep
        }

        if (substep.key === "color-insert-perforation") {
          let next = substep
          if (usableColorInsert) {
            next = overlayDefaultOntoSubstep(next, usableColorInsert) ?? next
          }
          if (usableNested || usablePerf) {
            const nestedDefault = usableNested || usablePerf
            next = {
              ...next,
              substeps: (next.substeps ?? []).map((nested) => {
                if (nested.key !== "perforation") return nested
                return (
                  overlayDefaultOntoSubstep(nested, nestedDefault) ?? nested
                )
              }),
            }
          }
          return next
        }

        return substep
      }),
    }
  })
}
